import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { AlertCircle, ArrowLeft, ArrowRight, Plus, Loader2, Copy, Check } from 'lucide-react';
import { api } from '@/lib/api';
import type { GrGameConfigCreateResult, GrGameConfigReviewStatus } from '@/lib/api';
import { useApproveGameConfig, useGameConfigs, useRejectGameConfig } from '../hooks';

// TODO: OAuth-app → game allowlist admin UI lives at the API level only for
// now (POST/GET/DELETE /v1/arches/game-rewards/admin/oauth-apps/:appId/games).
// Build a UI here when the LitCraft / app management page is added.

type StatusFilter = 'all' | GrGameConfigReviewStatus;

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

function reviewStatusBadgeVariant(
  status: GrGameConfigReviewStatus,
): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'approved':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'rejected':
      return 'destructive';
  }
}

export function AdminGamesPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const { gameConfigs, loading, error, refresh } = useGameConfigs(
    statusFilter === 'all' ? undefined : { reviewStatus: statusFilter },
  );
  const approveMutation = useApproveGameConfig();
  const rejectMutation = useRejectGameConfig();
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdGame, setCreatedGame] = useState<GrGameConfigCreateResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [gameName, setGameName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [minScore, setMinScore] = useState('0');
  const [maxScore, setMaxScore] = useState('100');

  const resetForm = () => {
    setGameName('');
    setDisplayName('');
    setDescription('');
    setMinScore('0');
    setMaxScore('100');
    setCreateError(null);
    setCreatedGame(null);
    setCopied(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameName.trim() || !displayName.trim()) return;

    setCreating(true);
    setCreateError(null);
    try {
      const result = await api.createGameRewardsGame({
        gameName: gameName.trim(),
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        minScore: Number(minScore),
        maxScore: Number(maxScore),
      });
      setCreatedGame(result);
      refresh();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create game');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!createdGame?.apiKey) return;
    try {
      await navigator.clipboard.writeText(createdGame.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be unavailable in some contexts (e.g. non-HTTPS)
      setCreateError('Failed to copy to clipboard. Please copy the key manually.');
    }
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleApprove = async (gameConfigId: string) => {
    setActingId(gameConfigId);
    setActionError(null);
    try {
      await approveMutation.mutateAsync({ gameConfigId });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve game');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (gameConfigId: string) => {
    setActingId(gameConfigId);
    setActionError(null);
    try {
      await rejectMutation.mutateAsync({ gameConfigId });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject game');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Game Configurations</h1>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Register Game
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{createdGame ? 'Game Registered' : 'Register New Game'}</DialogTitle>
              </DialogHeader>

              {createdGame ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Game <strong>{createdGame.gameName}</strong> has been registered. It starts in{' '}
                    <strong>pending</strong> review and cannot grant rewards until approved. Copy
                    the API key now — it will not be shown again.
                  </p>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <div className="flex gap-2">
                      <Input readOnly value={createdGame.apiKey} className="font-mono text-xs" />
                      <Button variant="outline" size="icon" onClick={handleCopyApiKey}>
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Prefix: {createdGame.apiKeyPrefix}
                    </p>
                  </div>
                  <Button className="w-full" onClick={() => handleDialogClose(false)}>
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="gameName">Game Name *</Label>
                    <Input
                      id="gameName"
                      value={gameName}
                      onChange={(e) => setGameName(e.target.value)}
                      placeholder="my_game"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Display Name *</Label>
                    <Input
                      id="displayName"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="My Game"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional description"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="minScore">Min Score</Label>
                      <Input
                        id="minScore"
                        type="number"
                        value={minScore}
                        onChange={(e) => setMinScore(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxScore">Max Score</Label>
                      <Input
                        id="maxScore"
                        type="number"
                        value={maxScore}
                        onChange={(e) => setMaxScore(e.target.value)}
                      />
                    </div>
                  </div>

                  {createError && <p className="text-sm text-destructive">{createError}</p>}

                  <Button type="submit" className="w-full" disabled={creating}>
                    {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Register Game
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter by review status">
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={statusFilter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(f.value)}
            aria-pressed={statusFilter === f.value}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {actionError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{actionError}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      ) : gameConfigs.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">
          {statusFilter === 'all'
            ? 'No games registered yet. Click "Register Game" to get started.'
            : `No ${statusFilter} games.`}
        </p>
      ) : (
        <div className="space-y-3">
          {gameConfigs.map((config) => {
            const isActing = actingId === config.gameConfigId;
            return (
              <Card key={config.gameConfigId} className="hover:bg-muted/50 transition-colors">
                <CardContent className="flex items-center justify-between py-4 gap-4">
                  <Link
                    to={`/a/arche_game_rewards/admin/games/${config.gameConfigId}`}
                    className="flex-1 space-y-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{config.displayName}</p>
                      <Badge variant={config.isActive ? 'default' : 'secondary'}>
                        {config.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge
                        variant={reviewStatusBadgeVariant(config.reviewStatus)}
                        data-testid={`review-status-${config.gameConfigId}`}
                      >
                        {config.reviewStatus}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {config.gameName} | Key prefix: {config.apiKeyPrefix} | Score range:{' '}
                      {config.minScore}-{config.maxScore}
                      {config.rewardMode === 'item_list' && ' | Mode: Item List'}
                    </p>
                  </Link>

                  {config.reviewStatus === 'pending' && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(config.gameConfigId)}
                        disabled={isActing}
                        aria-label={`Approve ${config.displayName}`}
                      >
                        {isActing && approveMutation.isPending ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : null}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(config.gameConfigId)}
                        disabled={isActing}
                        aria-label={`Reject ${config.displayName}`}
                      >
                        Reject
                      </Button>
                    </div>
                  )}

                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
